import { formatDistance } from 'date-fns';
import { isFunction } from 'lodash';
import { RotateCw } from 'lucide-react';

import { ShipmentLabel } from '@/components/modules/shipment/label';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import { InlineSpinner } from '@/components/ui/loading/inline-spinner';
import { SavedItemButton } from '@/components/ui/saved-item-button';
import { useCurrentDate } from '@/hooks/current-date-provider';
import { SAVED_ITEM_TYPE, SHARED, SHIPMENT_STATUS } from '@/lib/constants';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { ShareVariant } from '@/types';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { ShipmentStatusIndicator } from '../table/status-indicator';

export interface ShipmentSummaryProps {
  shipment: ShipmentModel;
  variant?: ShareVariant;
  onClickSavedItem?: () => void;
  savedItem?: SavedItemModel;
  isSavedItemsEnabled?: boolean;
  isLoading?: boolean;
  isRefreshing?: boolean;
  lastUpdatedDate?: string;
  onRequestRefresh?: () => void;
}

const ShipmentSummary = ({
  shipment,
  variant,
  onClickSavedItem,
  savedItem,
  isSavedItemsEnabled = false,
  lastUpdatedDate,
  isLoading = false,
  isRefreshing = false,
  onRequestRefresh,
}: ShipmentSummaryProps) => {
  const currentDate = useCurrentDate();

  const { status, subStatus, lastCheckedAt } = shipment;
  const shouldShowReload = variant !== SHARED && status !== SHIPMENT_STATUS.COMPLETED;
  const shouldShowStar = Boolean(isSavedItemsEnabled) && variant !== SHARED;
  const checkedAt = lastCheckedAt ? `Last checked ${formatDistance(new Date(lastCheckedAt), currentDate)} ago` : null;
  const updatedAt = lastUpdatedDate ? formatDistance(new Date(lastUpdatedDate), currentDate) : 'N/A';

  const handleRequestRefresh = () => {
    if (isFunction(onRequestRefresh)) onRequestRefresh();
    trackUser.event('Refresh shipment');
  };

  const { base, header, contextual, updatingText } = styles();

  return (
    <div className={base()}>
      <div className={header()}>
        {shouldShowStar && (
          <div className="-ml-2">
            <SavedItemButton
              onClick={() => (isFunction(onClickSavedItem) ? onClickSavedItem() : undefined)}
              type={SAVED_ITEM_TYPE.SHIPMENT}
              isSaved={!!savedItem}
            />
          </div>
        )}
        <ShipmentLabel shipment={shipment} variant="lg" />
        <div className="mx-2 max-w-40">
          <ShipmentStatusIndicator status={status} subStatus={subStatus} />
        </div>
      </div>

      <div className={contextual()}>
        {(isRefreshing || isLoading) && (
          <>
            <Text variant="secondary" className={updatingText()}>
              {isLoading ? 'Loading shipment details...' : 'Checking for updates...'}
            </Text>
            <InlineSpinner />
          </>
        )}
        {!isRefreshing && !isLoading && (
          <>
            <Text variant="secondary" className={updatingText()}>
              Updated {updatedAt} ago
            </Text>
            {shouldShowReload && (
              <Tooltip
                content={
                  <div className="p-1 text-left">
                    <Text variant="secondary" className="m-0">
                      Check for updates
                    </Text>
                    {checkedAt ? (
                      <Text variant="sm" className="m-0 opacity-60">
                        {checkedAt}
                      </Text>
                    ) : null}
                  </div>
                }
              >
                <button
                  onClick={handleRequestRefresh}
                  type="button"
                  className="grid size-7 place-items-center rounded-full bg-lightBlue-50 hover:bg-lightBlue-100"
                >
                  <RotateCw className="size-4 text-lightBlue-700" />
                  <span className="hidden">Refresh</span>
                </button>
              </Tooltip>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative flex flex-col justify-between border-b border-b-grey-300 p-4 lg:flex-row',
    header: 'flex items-center gap-1',
    contextual: 'mt-2 flex items-center gap-1 md:mt-0',
    updatingText: 'm-0 mr-1 flex items-center text-grey-500',
  },
});

export { ShipmentSummary };
